/**
 * chart-theme — shared ECharts token resolver for the Nexus design system.
 *
 * Rules (from DESIGN.md):
 *   - All color is read from CSS custom properties, never hard-coded.
 *   - A hidden probe element forces the browser to resolve OKLCH tokens into
 *     rgb() strings that ECharts (Canvas) can consume.
 *   - splitLine / axisLine → --border at low opacity (calm at rest).
 *   - axisLabel             → --text-subtle.
 *   - Operational lanes    → --data-detection / response / impact.
 *   - Anomalies / now line → --data-anomaly / --data-now.
 *   - Series palette        → --chart-1…6 (categorical, theme-aware).
 *   - Background            → transparent (card surface shows through).
 *   - Must react to theme switching: call resolveChartColors() again inside a
 *     MutationObserver watching document.documentElement's class attribute.
 */

/** Canvas-safe color strings resolved from CSS custom properties. */
export type ChartColors = {
  /** Optional for existing consumers; the resolver always supplies token-based chart typography. */
  typography?: { labelSize: number; fontFamily: string };
  /** --color-accent (brand-cyan) — primary single-series line color. */
  accent: string;
  /** accent at 20% opacity — area-fill top stop. */
  accentSoft: string;
  /** accent at 2% opacity — area-fill bottom stop. */
  accentFaint: string;
  /** --text-subtle — axis label ink. */
  label: string;
  /** --border at 80% — axis line / tick color. */
  axisLine: string;
  /** --border at 60% — split-line (grid) color. */
  grid: string;
  /** --color-surface at 95% — tooltip background. */
  tooltipBg: string;
  /** --color-primary — tooltip text. */
  tooltipText: string;
  /** --status-error — semantic anomaly/error markers. */
  statusError: string;
  /** --data-anomaly — anomaly markers; one colour whatever the anomaly's status. */
  anomaly: string;
  /** --data-now — achromatic "now" reference line. */
  now: string;
  /** Operational lane colors: Detection, Response, and Impact. */
  operational: {
    detection: string;
    response: string;
    impact: string;
  };
  /** --chart-1…6 — categorical multi-series palette (theme-aware OKLCH). */
  series: readonly [string, string, string, string, string, string];
  /** Block-safety classification; `safeSeries` is the stronger stroke for lines on tinted plots. */
  classification?: { safe: string; unsafe: string; safeSeries?: string };
  audience?: {
    safe: string;
    unsafe: string;
    blocking: string;
    /** Blocking periods that do not reach the scope on show. */
    blockingInactive?: string;
    plot: string;
    traffic: string;
    /** The counterfactual audience line: apart from the areas, the bands and the traffic line. */
    counterfactual?: string;
  };
  cdnsa?: { theftForRestreaming: string; cdnLeeching: string };
};

/** Injects an alpha channel into an rgb()/rgba() string from getComputedStyle. */
export function withAlpha(color: string, alpha: number): string {
  const match = color.match(/rgba?\(([^)]+)\)/);
  if (!match) return color;
  const [r, g, b] = match[1].split(",").map((p) => p.trim());
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Resolves all design-token colors that ECharts needs into canvas-safe rgb()
 * strings. A probe element is appended to `host` so the browser resolves
 * OKLCH custom properties through the full cascade (including .dark overrides),
 * then removed before returning.
 *
 * Call this:
 *   - on first chart render, passing the chart's container element.
 *   - again inside a MutationObserver callback when the theme class changes,
 *     then pass the result to `chart.setOption(buildOption(series, newColors))`.
 *
 * Why the canvas step: Chrome 111+ returns OKLCH strings from getComputedStyle
 * (e.g. "oklch(0.208 0.04 265.8)") which ECharts' Canvas renderer cannot parse.
 * Drawing through a 1×1 off-screen canvas converts any CSS color to rgb() so
 * withAlpha() and ECharts always receive a reliable rgba?() string.
 */
export function resolveChartColors(host: HTMLElement): ChartColors {
  const probe = document.createElement("span");
  probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none";
  host.appendChild(probe);

  // 1×1 canvas used to normalise OKLCH/HSL/etc → rgb() for Canvas rendering.
  const cvs = document.createElement("canvas");
  cvs.width = 1;
  cvs.height = 1;
  const ctx = cvs.getContext("2d", { willReadFrequently: true });

  const read = (token: string, fallback: string): string => {
    probe.style.color = `var(${token}, ${fallback})`;
    const raw = getComputedStyle(probe).color || fallback;
    // Already rgb/rgba (Firefox, older Chrome)? Return as-is.
    if (raw.startsWith("rgb") || !ctx) return raw;
    // Normalise OKLCH/HSL/etc → rgb() through canvas pixel readback.
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = raw;
    ctx.fillRect(0, 0, 1, 1);
    const d = ctx.getImageData(0, 0, 1, 1).data;
    return `rgb(${d[0]}, ${d[1]}, ${d[2]})`;
  };

  const accent = read("--brand-cyan", "rgb(1, 161, 229)");
  const label = read("--text-subtle", "rgb(100, 116, 139)");
  const border = read("--border", "rgb(100, 116, 139)");
  const surface = read("--surface", "rgb(15, 23, 42)");
  const primary = read("--text", "rgb(226, 232, 240)");
  const statusError = read("--status-error", "rgb(209, 75, 88)");
  const anomaly = read("--data-anomaly", "rgb(210, 90, 0)");
  const now = read("--data-now", "rgb(51, 65, 85)");
  const detection = read("--data-detection", "rgb(1, 161, 229)");
  const response = read("--data-response", "rgb(125, 47, 137)");
  const impact = read("--data-impact", "rgb(214, 0, 120)");

  // Categorical series fallbacks match tokens.css light-mode values.
  const s1 = read("--chart-1", "rgb(30, 140, 213)");
  const s2 = read("--chart-2", "rgb(71, 168, 115)");
  const s3 = read("--chart-3", "rgb(205, 165, 60)");
  const s4 = read("--chart-4", "rgb(142, 58, 155)");
  const s5 = read("--chart-5", "rgb(100, 80, 200)");
  const s6 = read("--chart-6", "rgb(193, 65, 75)");

  const safe = read("--classification-safe", "rgb(85, 185, 144)");
  const unsafe = read("--classification-unsafe", "rgb(209, 75, 88)");
  const safeSeries = read("--classification-safe-series", safe);

  probe.style.fontSize = "var(--widget-chart-label-size, 0.75rem)";
  const typography = {
    labelSize: Number.parseFloat(getComputedStyle(probe).fontSize) || 12,
    fontFamily: getComputedStyle(host).fontFamily || "Inter, ui-sans-serif, system-ui, sans-serif",
  };

  const audience = {
    safe: read("--audience-safe", s1),
    unsafe: read("--audience-unsafe", unsafe),
    blocking: read("--audience-blocking", safe),
    blockingInactive: read("--audience-blocking-inactive", label),
    plot: read("--audience-plot", surface),
    traffic: read("--audience-traffic", primary),
    counterfactual: read("--audience-counterfactual", accent),
  };
  const cdnsa = {
    theftForRestreaming: read("--cdnsa-theft-for-restreaming", "rgb(150, 60, 45)"),
    cdnLeeching: read("--cdnsa-cdn-leeching", "rgb(200, 155, 60)"),
  };

  host.removeChild(probe);

  return {
    typography,
    accent,
    accentSoft: withAlpha(accent, 0.08),
    accentFaint: withAlpha(accent, 0),
    label,
    axisLine: withAlpha(border, 0.8),
    grid: withAlpha(border, 0.6),
    tooltipBg: withAlpha(surface, 0.95),
    tooltipText: primary,
    statusError,
    anomaly,
    now,
    operational: { detection, response, impact },
    series: [s1, s2, s3, s4, s5, s6],
    classification: { safe, unsafe, safeSeries },
    audience,
    cdnsa,
  };
}
